import { useCallback, useEffect, useState } from "react";
import ImageGeneric from "@/assets/images/lg.png";
import ChatService from "@/services/ChatService";
import CadastroDeskService from "@/services/cadastroDesk";
import { Users, MessageCircle, Plus, Search } from "lucide-react";
import { NewChatModalImproved } from "./sections/NewChatModalImproved";
import { useLayout } from "@/contexts/UseLayout";
import { NavLink } from "react-router-dom";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { ResumoDesk } from "@/pages/UserGoOn/Desks/ResumoDesk";
import { getChatLastMessagePreview } from "@/utils/chatLastMessagePreview";

interface Props {
  idMenu: any;
}

export function Chat({ idMenu }: Props) {
  const { t } = useTranslation();
  const {
    chats,
    setOpenChats,
    getChats,
    searchChatsRecentes,
    setSearchChatsRecentes,
    userLogged,
  } = useLayout();
  const [searchMessage, setSearchMessage] = useState<string>("");
  const [newChat, setNewChat] = useState(false);
  const [filterType, setFilterType] = useState<"all" | "groups" | "individual">(
    "all",
  );
  const [openingChatId, setOpeningChatId] = useState<number | null>(null);
  const [openingDeskId, setOpeningDeskId] = useState<number | string | null>(null);
  const [deskCommentsData, setDeskCommentsData] = useState<any>(null);

  const debouncedSetSearchClientes: any = useCallback(
    debounce((value: string) => setSearchChatsRecentes(value), 500),
    [],
  );

  useEffect(() => {
    getChats();
  }, [searchChatsRecentes]);

  const handleOpenChat = async (room: any) => {
    if (openingChatId === room.id) return;
    setOpeningChatId(room.id);
    try {
      const res = await ChatService.recuperarSala(room.id);

      if (res?.data?.room) {
        const roomData = res.data.room;

        // Usar functional update para evitar stale closure do openChats
        setOpenChats((prev: any[]) => {
          if (prev.find((chat) => chat.room.id === roomData.id)) {
            return prev; // já está aberto, não duplicar
          }
          return [
            ...prev,
            {
              room: roomData,
              guest: roomData.users?.[0],
              unread_count: 0,
            },
          ];
        });
      }
    } catch (error) {
      console.error("Erro ao abrir conversa:", error);
    } finally {
      setNewChat(false);
      setOpeningChatId(null);
    }
  };

  const getChatDisplayName = (chat: any) => {
    if (chat.is_group) {
      return chat.name;
    } else {
      return (
        chat.name ||
        chat?.users?.[0]?.name ||
        t("chatModule.fallbacks.conversation")
      );
    }
  };

  const getChatImage = (chat: any) => {
    if (chat.is_group && chat.group_image) {
      return chat.group_image;
    } else {
      return chat?.users?.[0]?.url_img?.path ?? ImageGeneric;
    }
  };

  // Filtrar chats baseado no tipo selecionado
  const filteredChats =
    chats?.filter((chat: any) => {
      if (filterType === "all") return true;
      if (filterType === "groups") return chat.is_group;
      if (filterType === "individual") return !chat.is_group;
      return true;
    }) || [];

  // Contadores para os filtros
  const groupsCount = chats?.filter((chat: any) => chat.is_group).length || 0;
  const individualCount =
    chats?.filter((chat: any) => !chat.is_group).length || 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t("chatModule.dates.today");
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t("chatModule.dates.yesterday");
    } else {
      return date.toLocaleDateString("pt-BR");
    }
  };

  function inputSearchClientes(e: any) {
    debouncedSetSearchClientes(e?.target?.value);
    setSearchMessage(e?.target?.value);
  }

  const getDeskIdFromChat = (chat: any) => {
    const candidates = [
      chat?.addesk_id,
      chat?.id_addesk,
      chat?.desk_id,
      chat?.id_desk,
      chat?.addesk?.id,
      chat?.desk?.id,
      chat?.room?.addesk_id,
      chat?.room?.desk_id,
      chat?.room?.id_desk,
      chat?.metadata?.addesk_id,
      chat?.metadata?.desk_id,
    ];

    return candidates.find((value) => value !== null && value !== undefined && value !== "");
  };

  const getChatPreview = (chat: any) =>
    getChatLastMessagePreview(
      chat?.lastMessage ?? chat?.last_message,
      Number(userLogged?.data?.id),
      t("chatModule.fallbacks.no_messages"),
    );

  const handleOpenDeskComments = async (chat: any, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const deskId = getDeskIdFromChat(chat);
    if (!deskId || openingDeskId === deskId) return;

    setOpeningDeskId(deskId);
    try {
      const res = await CadastroDeskService.getDesk(deskId);
      if (res?.status === 200) {
        setDeskCommentsData(res.data);
      }
    } catch (error) {
      console.error("Erro ao abrir comentários da Desk:", error);
    } finally {
      setOpeningDeskId(null);
    }
  };

  return (
    <>
      <div>
        <div>
          <div className="mb-4 flex gap-5 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 lg:gap-5">
                <h2 className="text-base font-medium uppercase">
                  {t("chatModule.title")}
                </h2>
                <button
                  type="button"
                  className="transition-all p-1 rounded-full border border-[#153DCE] hover:scale-105 hover:border-[#153DCE]"
                  onClick={() => setNewChat(true)}
                >
                  <Plus size={15} color="#153DCE" />{" "}
                </button>
              </div>
              {/* <p className="text-xs -mt-1 text-cinza-300">
                {filteredChats?.length || 0} de {chats?.length || 0} conversas
              </p> */}
            </div>
          </div>

          {/* Filtros */}
          <div className="mb-4 flex flex-col gap-2 flex-wrap">
            <div className="flex justify-evenly sm:justify-between flex-wrap gap-1 my-3">
              <button
                onClick={() => setFilterType("all")}
                className={`flex flex-col items-center ${filterType === "all" ? "text-black menuNavItemsActive" : "text-black/20 menuNavItems"} hover:text-black text-xs transition-all`}
              >
                {t("chatModule.filters.all")} ({chats?.length || 0})
                <div className={`borderLabel`}></div>
              </button>
              <button
                onClick={() => setFilterType("individual")}
                className={`flex flex-col items-center ${filterType === "individual" ? "text-black menuNavItemsActive" : "text-black/20 menuNavItems"} hover:text-black text-xs transition-all`}
              >
                {/* <MessageCircle size={14} /> */}
                {t("chatModule.filters.private")} ({individualCount})
                <div className={`borderLabel`}></div>
              </button>
              <button
                onClick={() => setFilterType("groups")}
                className={`flex flex-col items-center ${filterType === "groups" ? "text-black menuNavItemsActive" : "text-black/20 menuNavItems"} hover:text-black text-xs transition-all`}
              >
                {/* <Users size={14} /> */}
                {t("chatModule.filters.ad_room")} ({groupsCount})
                <div className={`borderLabel`}></div>
              </button>
            </div>
            <div className="w-full relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 w-5 h-5" />
              <input
                type="text"
                value={searchMessage}
                onChange={inputSearchClientes}
                className="w-full rounded-[17px] pl-4 pr-10 py-2 duration-300 border-none ring-2 ring-black/10 focus:ring-azul-400 outline-none text-sm placeholder:text-black/30 text-black/30"
                placeholder={t("chatModule.search.placeholder")}
              />
            </div>
          </div>

          <ul className="space-y-3 max-h-[calc(90vh-130px)] h-full overflow-y-auto pr-4">
            {filteredChats?.length === 0 && (
              <li className="text-sm text-cinza-300 text-center py-8">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {filterType === "all"
                  ? t("chatModule.empty.no_conversations")
                  : filterType === "groups"
                    ? t("chatModule.empty.no_groups")
                    : t("chatModule.empty.no_individual_conversations")}
              </li>
            )}

            {filteredChats?.map((chat: any) => {
              const deskId = getDeskIdFromChat(chat);
              const isOpeningDesk = openingDeskId === deskId;

              return (
                <li key={chat.id} className="relative">
                  {/* Versão mobile - NavLink */}
                  <div className="flex sm:hidden items-center gap-2">
                    <NavLink
                      to={"/mensagens/chat"}
                      onClick={() => handleOpenChat(chat)}
                      className="flex justify-between items-center relative w-full min-w-0 transition-colors"
                    >
                      {chat?.unread_count > 0 && (
                        <span className="absolute top-8 right-0 bg-[#153ECE] text-white text-xs font-semibold rounded-full h-[10px] w-[10px] text-center"></span>
                      )}
                      <div className="flex items-center gap-3 w-full min-w-0">
                        <div className="relative">
                          <img
                            src={getChatImage(chat)}
                            alt={getChatDisplayName(chat)}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          {chat.is_group && (
                            <div className="absolute -bottom-1 -right-1 bg-azul-400 text-white rounded-full p-1">
                              <Users size={10} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] text-end pb-0.5">
                            <p>{formatDate(chat?.updated_at)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p
                              className={`font-semibold text-sm truncate text-black`}
                            >
                              {getChatDisplayName(chat)}
                            </p>
                            {chat.is_group && (
                              <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                                {t("chatModule.filters.ad_room")}
                              </span>
                            )}
                          </div>
                          <p
                            className={`text-xs line-clamp-1 ${
                              chat.unread_count > 0 ? "text-black" : "text-black/30"
                            }`}
                          >
                            {getChatPreview(chat)}
                          </p>
                        </div>
                      </div>
                    </NavLink>

                    {!!deskId && (
                      <button
                        type="button"
                        onClick={(event) => handleOpenDeskComments(chat, event)}
                        disabled={isOpeningDesk}
                        title="Abrir comentários da Desk"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-azul-400 text-azul-400 transition-all hover:bg-azul-400 hover:text-white disabled:cursor-wait disabled:opacity-60"
                      >
                        <MessageCircle size={16} />
                      </button>
                    )}
                  </div>

                  {/* Versão desktop */}
                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      onClick={() => handleOpenChat(chat)}
                      disabled={openingChatId === chat.id}
                      className={`flex justify-between items-center relative w-full transition-colors ${openingChatId === chat.id ? "opacity-60 cursor-wait" : ""}`}
                    >
                      {chat?.unread_count > 0 && (
                        <span className="absolute top-8 right-0 bg-[#153ECE] text-white text-xs font-semibold rounded-full h-[10px] w-[10px] text-center"></span>
                      )}
                      <div className="flex items-center gap-3 w-full">
                        <div className="relative">
                          <img
                            src={getChatImage(chat)}
                            alt={getChatDisplayName(chat)}
                            className="w-[54px] h-[54px] rounded-full object-cover"
                          />
                          {chat.is_group && (
                            <div className="absolute -bottom-1 -right-1 bg-azul-400 text-white rounded-full p-1">
                              <Users size={10} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="text-[10px] text-end pb-0.5 text-black/30">
                            <p>{formatDate(chat?.updated_at)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p
                              className={`font-medium text-sm line-clamp-2 text-black`}
                            >
                              {getChatDisplayName(chat)}
                            </p>
                            {chat?.is_group && (
                              <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                                {t("chatModule.filters.ad_room")}
                              </span>
                            )}
                          </div>
                          <p
                            className={`text-xs line-clamp-1 max-w-44 ${
                              chat?.unread_count > 0
                                ? "text-black"
                                : "text-black/30"
                            }`}
                          >
                            {getChatPreview(chat)}
                          </p>
                        </div>
                      </div>
                    </button>

                    {!!deskId && (
                      <button
                        type="button"
                        onClick={(event) => handleOpenDeskComments(chat, event)}
                        disabled={isOpeningDesk}
                        title="Abrir comentários da Desk"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-azul-400 text-azul-400 transition-all hover:bg-azul-400 hover:text-white disabled:cursor-wait disabled:opacity-60"
                      >
                        <MessageCircle size={16} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {newChat && (
          <NewChatModalImproved
            idMenu={idMenu}
            setNewChat={setNewChat}
            handleOpenChat={handleOpenChat}
          />
        )}

        {!!deskCommentsData && (
          <ResumoDesk
            togleOpen={setDeskCommentsData}
            data={deskCommentsData}
            client={deskCommentsData?.cliente?.name_fantasy}
            agency={deskCommentsData?.agencia?.name_fantasy}
            status={deskCommentsData?.status?.name}
            type="desk"
            initialTab="chat"
          />
        )}
      </div>
    </>
  );
}
